import { useEffect, useState, useCallback } from 'react';

import {
  alpha,
  Box,
  CircularProgress,
  makeStyles,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Help } from '@material-ui/icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useModal } from 'react-modal-hook';

import dayjsService from 'services/dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
  type AtendimentoInterfaceV2,
  atendimentosSelector,
  fetchAtendimentosEmAndamentoDoContato,
  fetchAtendimentosFinalizadosDoContato,
  resetAtendimentosEmAndamentoDoContato,
  resetAtendimentosFinalizadosDoContato,
  setAtendimentoDeAtividadeSelecionado,
} from 'store/reducers/atendimentos';
import { ContatoInterfaceV2 } from 'store/reducers/contatos';

import AtendimentoCard from './AtendimentoCard';
import AtendimentoCardSkeleton from './AtendimentoCardSkeleton';
import FilterOptions, {
  FilterOptions as FilterOptionsType,
} from './FilterOptions';
import ModalHistoricoAtendimentoContato from './ModalHistoricoAtendimentoContato';

export type PayloadType = {
  contatoId: string;
  cursor?: CursorInterface;
  dateFinishedStart?: string;
  dateFinishedEnd?: string;
  tagIds?: string[];
  channelCodes?: string[];
};

interface TabPanelAtividadesContatoProps {
  contato: ContatoInterfaceV2;
}

const SCROLL_CONTAINER_ID = 'scrollAtividadesContato';

const useStyles = makeStyles(({ typography, palette, spacing }) => ({
  headingSection: {
    fontWeight: typography.fontWeightMedium as number,
  },
  tooltip: {
    backgroundColor: palette.background.tertiary,
    color: palette.text.primary,
    fontWeight: 500,
    fontSize: 12,
    padding: spacing(1),
    width: spacing(29.1),
    '& .MuiTooltip-arrow': { color: palette.background.tertiary },
  },
  scrollContainer: {
    overflowY: 'auto',
    height: '100%',
    minHeight: 0,
  },
}));

function SectionHeader({
  title,
  color,
  tooltipText,
}: {
  title: string;
  color: string;
  tooltipText: string;
}) {
  const classes = useStyles();

  return (
    <Box display="flex" alignItems="center" gridGap={8}>
      <Typography variant="body2" style={{ color, fontWeight: 600 }}>
        {title}
      </Typography>

      <Tooltip
        title={tooltipText}
        arrow
        placement="left"
        classes={{ tooltip: classes.tooltip }}
      >
        <Help fontSize="small" color="disabled" />
      </Tooltip>
    </Box>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height={80}>
      <Typography variant="body2" color="textSecondary">
        {message}
      </Typography>
    </Box>
  );
}

export default function TabPanelAtividadesContato({
  contato,
}: TabPanelAtividadesContatoProps) {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [filters, setFilters] = useState<Omit<FilterOptionsType, 'page'>>({
    rangeDate: [],
    tags: [],
    channelCode: '',
  });

  const {
    atendimentoDeAtividadeSelecionado,
    atendimentosEmAndamentoDoContato,
    atendimentosFinalizadosDoContato,
    loadingAtendimentosEmAndamentoDoContato,
    loadingAtendimentosFinalizadosDoContato,
    atendimentosFinalizadosDoContatoCursor,
  } = useAppSelector(atendimentosSelector);

  const [isAtendimentoEmAndamento, setIsAtendimentoEmAndamento] =
    useState(false);

  const getAtendimentosFinalizados = useCallback(
    (params?: {
      cursor?: CursorInterface;
      currentFilters?: typeof filters;
    }) => {
      if (!contato?.id) return;

      const data: PayloadType = {
        contatoId: contato.id,
        dateFinishedStart: params?.currentFilters?.rangeDate[0]
          ? dayjsService(params?.currentFilters?.rangeDate[0]).format(
              'YYYY-MM-DD',
            )
          : undefined,
        dateFinishedEnd: params?.currentFilters?.rangeDate[1]
          ? dayjsService(params?.currentFilters?.rangeDate[1]).format(
              'YYYY-MM-DD',
            )
          : undefined,
        tagIds: params?.currentFilters?.tags.map(tag => tag.id) as string[],
        cursor: params?.cursor,
      };

      if (params?.currentFilters?.channelCode) {
        data.channelCodes = [params?.currentFilters?.channelCode];
      }

      dispatch(
        fetchAtendimentosFinalizadosDoContato({
          ...data,
        }),
      );
    },
    [contato?.id, dispatch],
  );

  const fetchNextAtendimentos = useCallback(() => {
    const { afterCursor } = atendimentosFinalizadosDoContatoCursor;

    if (afterCursor) {
      getAtendimentosFinalizados({
        currentFilters: filters,
        cursor: { afterCursor },
      });
    }
  }, [
    atendimentosFinalizadosDoContatoCursor,
    filters,
    getAtendimentosFinalizados,
  ]);

  const handleFilterChange = useCallback((newFilters: FilterOptionsType) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const [showModalHistoricoAtendimento, hideModalHistoricoAtendimento] =
    useModal(
      ({ in: open }) =>
        atendimentoDeAtividadeSelecionado ? (
          <ModalHistoricoAtendimentoContato
            open={open}
            atendimento={{
              ...atendimentoDeAtividadeSelecionado,
              contact: contato,
            }}
            isAtendimentoEmAndamento={isAtendimentoEmAndamento}
            onClose={hideModalHistoricoAtendimento}
          />
        ) : null,
      [atendimentoDeAtividadeSelecionado, isAtendimentoEmAndamento, contato],
    );

  const handleHistoricoAtendimento = useCallback(
    (
      atendimento: AtendimentoInterfaceV2,
      isAtendimentoEmAndamento: boolean,
    ) => {
      dispatch(
        setAtendimentoDeAtividadeSelecionado({
          ...atendimento,
          contact: contato,
        }),
      );
      setIsAtendimentoEmAndamento(isAtendimentoEmAndamento);
      showModalHistoricoAtendimento();
    },
    [contato, dispatch, showModalHistoricoAtendimento],
  );

  useEffect(() => {
    if (contato?.id) {
      dispatch(resetAtendimentosFinalizadosDoContato());
      dispatch(resetAtendimentosEmAndamentoDoContato());

      dispatch(
        fetchAtendimentosEmAndamentoDoContato({ contatoId: contato?.id }),
      );
      getAtendimentosFinalizados();
    }

    return () => {
      dispatch(resetAtendimentosFinalizadosDoContato());
      dispatch(resetAtendimentosEmAndamentoDoContato());
    };
  }, [contato?.id, dispatch, getAtendimentosFinalizados]);

  const hasAtendimentosEmAndamento =
    atendimentosEmAndamentoDoContato.length > 0;
  const hasAtendimentosFinalizados =
    atendimentosFinalizadosDoContato.length > 0;

  return (
    <Box
      id={SCROLL_CONTAINER_ID}
      className={classes.scrollContainer}
      display="flex"
      flexDirection="column"
      gridGap={16}
    >
      <Box display="flex" flexDirection="column" gridGap={16}>
        <SectionHeader
          title="Atendimentos em andamento"
          color={theme.palette.success.main}
          tooltipText="Visualize o histórico de mensagens nos canais que você possui acesso."
        />

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gridGap={8}
        >
          {loadingAtendimentosEmAndamentoDoContato &&
            Array.from({ length: 2 }).map((_, index) => (
              <AtendimentoCardSkeleton key={`${index + 1}`} />
            ))}

          {!loadingAtendimentosEmAndamentoDoContato &&
            !hasAtendimentosEmAndamento && (
              <EmptyState message="Nenhum atendimento em andamento" />
            )}

          {hasAtendimentosEmAndamento &&
            atendimentosEmAndamentoDoContato.map(atendimento => (
              <AtendimentoCard
                key={atendimento.id}
                atendimento={atendimento}
                isAtendimentoEmAndamento
                onClick={() => handleHistoricoAtendimento(atendimento, true)}
              />
            ))}
        </Box>
      </Box>

      <InfiniteScroll
        dataLength={atendimentosFinalizadosDoContato.length}
        next={fetchNextAtendimentos}
        scrollableTarget={SCROLL_CONTAINER_ID}
        hasMore={Boolean(atendimentosFinalizadosDoContatoCursor.afterCursor)}
        loader={<CircularProgress size={24} />}
        style={{ overflow: 'visible' }}
      >
        <Box display="flex" flexDirection="column" gridGap={8}>
          <Box style={{ marginBottom: 8 }}>
            <SectionHeader
              title="Histórico de atendimentos"
              color={alpha(theme.palette.primary.main, 0.7)}
              tooltipText="Visualize o histórico de mensagens nos canais que você possui acesso."
            />
          </Box>

          <FilterOptions
            contatoId={contato?.id}
            onFilterChange={handleFilterChange}
          />

          {loadingAtendimentosFinalizadosDoContato &&
            atendimentosFinalizadosDoContato.length === 0 &&
            Array.from({ length: 3 }).map((_, index) => (
              <AtendimentoCardSkeleton key={`${index + 1}`} />
            ))}

          {hasAtendimentosFinalizados &&
            atendimentosFinalizadosDoContato.map(atendimento => (
              <AtendimentoCard
                key={atendimento.id}
                atendimento={atendimento}
                onClick={() => handleHistoricoAtendimento(atendimento, false)}
              />
            ))}

          {!loadingAtendimentosFinalizadosDoContato &&
            !hasAtendimentosFinalizados && (
              <EmptyState message="Nenhum atendimento finalizado" />
            )}
        </Box>
      </InfiniteScroll>
    </Box>
  );
}
